import { useEffect, useState } from "react";
import {
  JOB_TYPE_LABELS,
  CANCELLATION_REASON_LABELS,
  type Job,
  type CancellationReason,
} from "@eagledispatch/shared";
import { apiFetch, ApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import Button from "../../components/Button";
import Card from "../../components/Card";
import StatusTag from "../../components/StatusTag";
import FieldError from "../../components/FieldError";
import LoadPhotosSection from "../../components/office-detail/LoadPhotosSection";
import LoadDecisionEditor from "../../components/office-detail/LoadDecisionEditor";
import type { OfficeRoute } from "./OfficeHome";

const CANCELLATION_REASONS = Object.keys(
  CANCELLATION_REASON_LABELS,
) as CancellationReason[];

/**
 * Office Active Job Detail view (Unit #20 + Unit #26).
 *
 * PM locked: Office can CANCEL or REASSIGN active jobs (NEW or IN_PROGRESS).
 * Office NEVER assigns (no assign button, no assign form — dispatcher assigns).
 */
export default function OfficeActiveJobDetail({
  id,
  onNavigate,
  onToast,
}: {
  id: string;
  onNavigate: (route: OfficeRoute) => void;
  onToast?: (msg: string, kind?: "success" | "error") => void;
}) {
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Action Panel State: null | "reassign_confirm" | "cancel" | "pause"
  const [actionPanel, setActionPanel] = useState<
    "reassign_confirm" | "cancel" | "pause" | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Cancel Form State
  const [cancelReason, setCancelReason] = useState<CancellationReason | "">("");
  const [cancelNote, setCancelNote] = useState("");

  // Pause/Resume state (Unit #25)
  const [pauseNote, setPauseNote] = useState("");

  const loadJob = () => {
    apiFetch<Job>(`/api/v1/jobs/${id}`)
      .then((j) => {
        setJob(j);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => {
    loadJob();
  }, [id]);

  const handleReassign = async () => {
    if (!job) return;
    setActionLoading(true);
    setActionError(null);

    try {
      await apiFetch<Job>(`/api/v1/jobs/${job.id}/reassign`, {
        method: "POST",
        body: JSON.stringify({}),
      });

      setActionPanel(null);
      loadJob();
      if (onToast) onToast(`Job ${job.jobNumber} unassigned and returned to NEW.`);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job) return;

    if (!cancelReason) {
      setFieldErrors({ reason: "Reason is required" });
      return;
    }
    if (cancelReason === "OTHER_WITH_NOTE" && !cancelNote.trim()) {
      setFieldErrors({ note: "Note is required for 'Other' reason" });
      return;
    }

    setActionLoading(true);
    setActionError(null);
    setFieldErrors({});

    try {
      await apiFetch<Job>(`/api/v1/jobs/${job.id}/cancel`, {
        method: "POST",
        body: JSON.stringify({
          reason: cancelReason,
          note: cancelNote.trim() ? cancelNote.trim() : undefined,
        }),
      });

      if (onToast) onToast(`Job ${job.jobNumber} cancelled.`);
      onNavigate({ name: "home" });
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (Object.keys(err.fieldErrors).length > 0) {
          setFieldErrors(err.fieldErrors);
        } else {
          setActionError(err.message);
        }
      } else {
        setActionError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    if (!job) return;
    setActionLoading(true);
    setActionError(null);

    try {
      await apiFetch<Job>(`/api/v1/jobs/${job.id}/pause`, {
        method: "POST",
        body: JSON.stringify({ note: pauseNote.trim() || undefined }),
      });

      setActionPanel(null);
      setPauseNote("");
      loadJob();
      if (onToast) onToast(`Job ${job.jobNumber} paused — driver freed.`);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    if (!job) return;
    setActionLoading(true);
    setActionError(null);

    try {
      await apiFetch<Job>(`/api/v1/jobs/${job.id}/resume`, {
        method: "POST",
        body: JSON.stringify({}),
      });

      setActionPanel(null);
      loadJob();
      if (onToast) onToast(`Job ${job.jobNumber} resumed — driver restored.`);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (Object.keys(err.fieldErrors).length > 0) {
          setFieldErrors(err.fieldErrors);
        } else {
          setActionError(err.message);
        }
      } else {
        setActionError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const back = () => onNavigate({ name: "home" });

  if (error) {
    return (
      <main className="surface office-surface">
        <header className="surface-header">
          <Button variant="ghost" onClick={back}>
            ← Back
          </Button>
          <h1>Active Job</h1>
        </header>
        <Card>
          <p className="err">Failed to load job.</p>
          <p className="muted">{error}</p>
        </Card>
      </main>
    );
  }

  if (!job) {
    return (
      <main className="surface office-surface">
        <header className="surface-header">
          <Button variant="ghost" onClick={back}>
            ← Back
          </Button>
          <h1>Active Job</h1>
        </header>
        <p className="muted">Loading…</p>
      </main>
    );
  }

  const snap = (job.customerSnapshot ?? {}) as {
    customer?: Record<string, unknown>;
    property?: Record<string, unknown>;
  };
  const customer = (snap.customer ?? {}) as Record<string, unknown>;
  const property = (snap.property ?? {}) as Record<string, unknown>;

  const address = [property.addressLine1, property.city, property.state, property.zip]
    .filter(Boolean)
    .join(", ");

  const canReassign = (job.state === "NEW" && !!job.driver) || job.state === "IN_PROGRESS" || job.state === "PAUSED";
  const canCancel = job.state === "NEW" || job.state === "IN_PROGRESS" || job.state === "PAUSED";
  const canPause = job.state === "IN_PROGRESS";
  const canResume = job.state === "PAUSED";

  return (
    <main className="surface office-surface">
      <header className="surface-header">
        <Button variant="ghost" onClick={back}>
          ← Back
        </Button>
        <h1>{job.jobNumber}</h1>
        <StatusTag state={job.state} />
      </header>

      {/* Office Job Actions (CANCEL, REASSIGN, PAUSE, RESUME — NO ASSIGN) */}
      {(canReassign || canCancel || canPause || canResume) && (
        <Card>
          <h2>Office Actions</h2>

          {actionError && <p className="form-error err">{actionError}</p>}

          {/* Default Button Bar */}
          {actionPanel === null && (
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px" }}>
              {canReassign && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setActionPanel("reassign_confirm");
                    setActionError(null);
                  }}
                >
                  Reassign Job…
                </Button>
              )}
              {canCancel && (
                <Button
                  variant="ghost"
                  style={{ color: "var(--red)" }}
                  onClick={() => {
                    setActionPanel("cancel");
                    setActionError(null);
                    setFieldErrors({});
                  }}
                >
                  Cancel Job…
                </Button>
              )}
              {canPause && (
                <Button
                  variant="secondary"
                  style={{ color: "#8A6D00" }}
                  onClick={() => {
                    setActionPanel("pause");
                    setActionError(null);
                    setPauseNote("");
                  }}
                >
                  Pause Job…
                </Button>
              )}
              {canResume && (
                <Button
                  variant="primary"
                  onClick={handleResume}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Resuming…" : "Resume Job"}
                </Button>
              )}
            </div>
          )}

          {/* REASSIGN CONFIRM PANEL */}
          {actionPanel === "reassign_confirm" && (
            <div className="banner banner-warning" style={{ marginTop: "10px" }}>
              <p style={{ margin: "0 0 10px 0" }}>
                <strong>Pull job back from {job.driver?.name || "driver"}?</strong>
                <br />
                The job will return to <code>NEW</code> and unassigned. The dispatcher can then assign a different driver.
              </p>
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  variant="secondary"
                  onClick={handleReassign}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Reassigning…" : "Yes, Pull Back"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActionPanel(null)}
                  disabled={actionLoading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* CANCEL PANEL */}
          {actionPanel === "cancel" && (
            <form onSubmit={handleCancel} className="admin-inline-form" style={{ marginTop: "10px" }}>
              <h3>Cancel Job</h3>
              <div className="form-group">
                <label>Cancellation Reason*</label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value as CancellationReason)}
                  disabled={actionLoading}
                >
                  <option value="">Select a reason…</option>
                  {CANCELLATION_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {CANCELLATION_REASON_LABELS[r]}
                    </option>
                  ))}
                </select>
                <FieldError message={fieldErrors.reason} />
              </div>

              {cancelReason === "OTHER_WITH_NOTE" && (
                <div className="form-group">
                  <label>Cancellation Note*</label>
                  <textarea
                    rows={2}
                    value={cancelNote}
                    onChange={(e) => setCancelNote(e.target.value)}
                    placeholder="Provide details for cancellation…"
                    disabled={actionLoading}
                  />
                  <FieldError message={fieldErrors.note} />
                </div>
              )}

              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Button
                  type="submit"
                  variant="primary"
                  style={{ background: "var(--red)", borderColor: "var(--red)", color: "#ffffff" }}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Cancelling…" : "Confirm Cancel"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setActionPanel(null)}
                  disabled={actionLoading}
                >
                  Back
                </Button>
              </div>
            </form>
          )}

          {/* PAUSE PANEL */}
          {actionPanel === "pause" && (
            <div style={{ marginTop: "10px" }}>
              <h3>Pause Job</h3>
              <p className="muted" style={{ fontSize: "13px", marginBottom: "8px" }}>
                This frees the driver immediately so they can take a priority job.
                The same driver will be restored when you resume.
              </p>
              <div className="form-group">
                <label>Note (optional)</label>
                <textarea
                  rows={2}
                  value={pauseNote}
                  onChange={(e) => setPauseNote(e.target.value)}
                  placeholder="e.g. redirecting to ED-0042"
                  disabled={actionLoading}
                  maxLength={500}
                />
              </div>
              {actionError && <p className="form-error err">{actionError}</p>}
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Button
                  variant="secondary"
                  style={{ color: "#8A6D00" }}
                  onClick={handlePause}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Pausing…" : "Pause Job"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setActionPanel(null)}
                  disabled={actionLoading}
                >
                  Back
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Card>
        <h2>Customer</h2>
        <dl className="detail-list">
          <div className="detail-row">
            <dt>Company</dt>
            <dd>{String(customer.companyName ?? "—")}</dd>
          </div>
          <div className="detail-row">
            <dt>Site</dt>
            <dd>{String(property.siteName ?? "—")}</dd>
          </div>
          <div className="detail-row">
            <dt>Address</dt>
            <dd>{address || "—"}</dd>
          </div>
        </dl>
      </Card>

      <Card>
        <h2>Job Details</h2>
        <dl className="detail-list">
          <div className="detail-row">
            <dt>Size</dt>
            <dd>{job.dumpsterSize?.label ?? "—"}</dd>
          </div>
          <div className="detail-row">
            <dt>Type</dt>
            <dd>{JOB_TYPE_LABELS[job.jobType]}</dd>
          </div>
          <div className="detail-row">
            <dt>Truck</dt>
            <dd>{job.truck ? `Truck ${job.truck.unitNumber}` : "Unassigned"}</dd>
          </div>
          <div className="detail-row">
            <dt>Driver</dt>
            <dd>{job.driver?.name ?? "Unassigned"}</dd>
          </div>
        </dl>
      </Card>

      {job.message ? (
        <Card>
          <h2>Dispatcher Message</h2>
          <p className="job-message">{job.message}</p>
        </Card>
      ) : null}

      {/* Unit #26: LOAD PHOTOS section */}
      <LoadPhotosSection jobId={id} />

      {/* Unit #26: LOAD DECISION panel — SPEC-55: set only after review */}
      <LoadDecisionEditor
        job={job}
        onUpdated={setJob}
        onToast={onToast}
        editable={job.state === "REVIEWED"}
      />

      <Card>
        <h2>Timeline</h2>
        <dl className="detail-list">
          <div className="detail-row">
            <dt>Created</dt>
            <dd>{formatDateTime(job.createdAt)}</dd>
          </div>
          <div className="detail-row">
            <dt>Started</dt>
            <dd>{formatDateTime(job.startAt)}</dd>
          </div>
          <div className="detail-row">
            <dt>Finished</dt>
            <dd>{formatDateTime(job.finishAt)}</dd>
          </div>
        </dl>
      </Card>
    </main>
  );
}
